/**
 * A git remote as an identity (§5.25).
 *
 * A project an organization shares is a set of repositories, and the one thing
 * about a repository that means the same on every machine is where it is
 * pushed. Its folder does not — that is chosen by whoever clones it — and its
 * local id does not either. So a remote is what a checkout is matched on when a
 * member already has one, and what a clone is made from when they do not.
 *
 * Two spellings of one repository have to be one identity:
 *
 *   git@github.com:Acme/api.git   https://github.com/acme/api   ssh://git@github.com/acme/api
 *
 * all read as `github.com/acme/api`. The identity drops the scheme, the user,
 * the port and the `.git`; on the three hosts that treat paths
 * case-insensitively it is lower-cased as well. A self-hosted host keeps its
 * path's case, because nothing here knows what that server does with it.
 *
 * What is refused is what nobody else could clone: a path on this machine, a
 * `file://` URL, anything with whitespace or control characters. And a URL is
 * never kept with a password in it — `https://user:token@host/…` is a
 * credential in a document every member reads.
 *
 * Nor is a remote ever read that git could take for an option: one starting
 * with `-`, or a login (an scp-like or ssh user) outside `LOGIN`. An
 * organization's document hands `cloneUrl` to git on every member's machine,
 * and `--upload-pack=<command>;x@host:path` reads as scp-like — a command run
 * on each of them by the first fetch that forgot its `--`.
 */

export type RepoHost = 'github' | 'gitlab' | 'bitbucket' | 'other';

export interface RemoteIdentity {
  /** `github.com/acme/api` — what two checkouts are compared on. */
  remote: string;
  host: RepoHost;
  /** A URL git can clone, as the publisher's remote spelled it, minus any credential. */
  cloneUrl: string;
}

const KNOWN: Record<string, RepoHost> = {
  'github.com': 'github',
  'gitlab.com': 'gitlab',
  'bitbucket.org': 'bitbucket'
};

/** Longer than any remote a person pushes to, and short enough to refuse a document pasted in by mistake. */
const MAX_REMOTE_CHARS = 2048;

// eslint-disable-next-line no-control-regex -- a control character in a remote is not one.
const UNPRINTABLE = /[\s\x00-\x1f\x7f]/;

/**
 * `<transport>::<address>`, git's remote-helper form, which the scp-like
 * reading below would otherwise take for a host called `ext` (§5.25). `ext::`
 * runs its address as a command and any other name runs `git-remote-<name>`:
 * refused here, and `protocol.ext.allow=never` on every host git besides
 * (`HOST_GIT_CONFIG`, desktop `git-host.ts`).
 */
const HELPER = /^[a-z][a-z0-9+.-]*::/i;

/** The account an scp-like or ssh remote logs in as: what such a login is made of, and never a leading `-`. */
const LOGIN = /^[A-Za-z0-9._~][A-Za-z0-9._~-]*$/;

/** The identity of a remote URL, or `null` when it names nothing another machine could clone. */
export function normalizeRemote(url: string): RemoteIdentity | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed || trimmed.length > MAX_REMOTE_CHARS || UNPRINTABLE.test(trimmed)) return null;
  if (HELPER.test(trimmed) || trimmed.startsWith('-')) return null;

  const parts = scpLike(trimmed) ?? urlLike(trimmed);
  if (!parts) return null;

  const hostname = parts.hostname.toLowerCase();
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/.test(hostname)) return null;
  const host = KNOWN[hostname] ?? 'other';

  const segments = parts.path
    .replace(/\\/g, '/')
    .split('/')
    .filter((segment) => segment && segment !== '.');
  if (segments.some((segment) => segment === '..')) return null;
  if (segments.length) segments[segments.length - 1] = segments[segments.length - 1].replace(/\.git$/i, '');
  const path = segments.filter(Boolean).join('/');
  // A known host addresses a repository as `owner/name` at least — GitLab
  // subgroups add levels, never remove one. Elsewhere one segment is enough.
  if (!path || (host !== 'other' && path.split('/').length < 2)) return null;

  return {
    remote: `${hostname}/${host === 'other' ? path : path.toLowerCase()}`,
    host,
    cloneUrl: parts.cloneUrl
  };
}

/**
 * The https URL of a repository on one of the known hosts — what a clone is made
 * from when the host's own sign-in is the credential (§5.16). `null` elsewhere:
 * a self-hosted server is cloned the way its publisher pushed to it.
 */
export function httpsCloneUrl(identity: Pick<RemoteIdentity, 'remote' | 'host'>): string | null {
  return identity.host === 'other' ? null : `https://${identity.remote}.git`;
}

/** Whether two remote URLs name the same repository. */
export function sameRemote(a: string, b: string): boolean {
  const left = normalizeRemote(a);
  const right = normalizeRemote(b);
  return left !== null && right !== null && left.remote === right.remote;
}

/**
 * `user@host:path` — git's scp-like form, which is not a URL and has to be read
 * before `new URL()` mistakes the user for a scheme. A Windows drive (`C:\…`)
 * has a one-letter "host" and is refused: it is a folder, not a remote.
 */
function scpLike(value: string): { hostname: string; path: string; cloneUrl: string } | null {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return null;
  const match = /^(?:([^@/\s:]+)@)?([^:/\s]+):(?!\/\/)(.+)$/.exec(value);
  if (!match || match[2].length < 2) return null;
  if (match[1] !== undefined && !LOGIN.test(match[1])) return null;
  return { hostname: match[2], path: match[3], cloneUrl: value };
}

function urlLike(value: string): { hostname: string; path: string; cloneUrl: string } | null {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  if (!/^(https?|ssh|git):$/.test(parsed.protocol) || !parsed.hostname) return null;
  // The user of an ssh URL is the account the key logs in as, not a secret. An
  // https user, with or without a password, is where a token gets pasted.
  if (parsed.protocol !== 'ssh:') parsed.username = '';
  else if (parsed.username && !LOGIN.test(decodeURIComponent(parsed.username))) return null;
  parsed.password = '';
  parsed.search = '';
  parsed.hash = '';
  return { hostname: parsed.hostname, path: decodeURIComponent(parsed.pathname), cloneUrl: parsed.toString() };
}
